import { Weapon } from '../Weapon.js';
import { WEAPONS } from '../../../../config/constants.js';

/**
 * Umbral Lantern (Act II, ACT2-BIBLE.md §7) — a short beam that also carries
 * a light.
 *
 * The beam is a line of overlapping stationary segments re-struck every
 * `fireRate`, each living just longer than the interval so the line reads as
 * continuous. Modelling it as ordinary pooled projectiles (rather than a beam
 * primitive) keeps it on the same collision, crit, DoT and damage-multiplier
 * path as every other weapon — the same reasoning Nova Pulse's rings use.
 * It has no travel time and no aim-ahead, and its reach is arm's length: the
 * trade is that you have to be in the boss's face to use it at all.
 *
 * The light is the part nothing else has. `lightRadius` is read by
 * Game._drawGloaming and widens the player's lit disc in a darkness fight,
 * which makes the Lantern a genuine build answer to the Gloam (and to the
 * Undersong's gloaming phase) rather than just another damage number. It
 * costs nothing in every other fight — no one else darkens the arena.
 */
export class UmbralLantern extends Weapon {
  constructor() {
    super(WEAPONS.umbralLantern);
  }

  /** Reach grows slowly with level — it stays a close-quarters weapon. */
  get range() {
    return this.config.range + this.level * 2;
  }

  /** How far the lantern lights the dark (see Game._drawGloaming). */
  get lightRadius() {
    return this.config.lightRadius + this.level * 3;
  }

  get segmentCount() {
    return this.config.segments + (this.level >= 5 ? 1 : 0);
  }

  fire(origin, target, pool) {
    if (!target) return;
    const angle = Math.atan2(target.y - origin.y, target.x - origin.x);
    const damage = this.config.damage + this.level * 0.9;
    const segments = this.segmentCount;
    const step = this.range / segments;
    for (let i = 1; i <= segments; i++) {
      const dist = step * i;
      pool.fire({
        x: origin.x + Math.cos(angle) * dist,
        y: origin.y + Math.sin(angle) * dist,
        radius: this.config.projectileRadius,
        // NB: the segments are independent projectiles, so a target big
        // enough to span several of them (any boss — 4-5 of the 6 segments
        // overlap an 18px boss hull at contact range) takes ONE SEGMENT'S
        // DAMAGE PER OVERLAPPING SEGMENT, every fire. The "one enemy per
        // tick" rule in CombatSystem._checkCollisions is per projectile, and
        // does not collapse the line into a single hit. That makes the beam's
        // real output several times this number against a boss and roughly
        // this number against a minion — the reason it out-damages its config
        // read by ~4x on the Harmonics bench (fusions/dpsBench.js).
        damage,
        hits: 1,
        lifetime: this.config.lifetime,
        color: this.config.color,
      });
    }
  }
}
